/**
 * Candles and sparklines from a real trade tape.
 *
 * The output shape and the three invariants are the ones `src/lib/demo/series.ts`
 * documents, because the chart component cannot tell the two sources apart:
 *
 *  1. the last close EQUALS the token's live spot price;
 *  2. buckets with no trades carry the previous close forward at zero volume, so
 *     the series is gapless and the x-axis is uniform;
 *  3. the `floor` track is monotonically non-decreasing and never above the low.
 *
 * (3) is not cosmetic. `redeemAtFloor` is a standing bid nobody can cancel, so a
 * print below the floor is not a thing that can happen — which is why lows are
 * clamped UP to the floor rather than the floor being clamped down to the low.
 *
 * ## Where the floor track comes from
 *
 * A `Trade` log's `floorAmount` tells us ETH went into the reserve but not what
 * the resulting floor PRICE was, and `DemoTrade` only carries `floorLiftEth`. So
 * the track is anchored on the one floor we know exactly — the token's current
 * `floorPrice` — and each bucket is placed at that floor times its share of
 * cumulative lift ETH. Every step therefore corresponds to a real on-chain lift
 * and the track lands exactly on the reported floor; only the apportionment
 * between steps is proportional rather than accounting-exact. An honest
 * reconstruction, not a fabrication.
 */

import { weiToNumber } from '@/lib/format';
import { byChainOrder } from './reader';
import { INTERVAL_COUNT, INTERVAL_MS } from '@/lib/demo/series';
import type { Candle, CandleInterval, DemoToken, DemoTrade } from '@/lib/demo/types';

const DAY = 86_400_000;
const SPARK_POINTS = 48;

/**
 * Bucketed OHLCV from the tape.
 *
 * `trades` may be in any order; it is sorted here. Passing a tape that does not
 * reach back over the whole window is fine — the series simply starts at the
 * first bucket that has data (or at the token's creation, whichever is later).
 */
export function buildCandlesFromTrades(
  trades: readonly DemoTrade[],
  interval: CandleInterval,
  token: DemoToken,
  opts: { count?: number; now?: number } = {},
): Candle[] {
  const bucket = INTERVAL_MS[interval];
  const want = opts.count ?? INTERVAL_COUNT[interval];
  const now = opts.now ?? Date.now();
  const spot = weiToNumber(token.spotPrice);
  const floorNow = weiToNumber(token.floorPrice);

  // Ascending CHAIN order, not timestamp order: a block holds many trades and
  // they all share its timestamp, so a `ts` sort scrambles same-block fills and
  // the bucket's open/close come out of sequence. See `byChainOrder`.
  const priced = trades.filter((t) => t.price > 0).sort(byChainOrder).reverse();

  const lastOpen = Math.floor(now / bucket) * bucket;
  // Never draw bars that predate the token, and never more than the window wants.
  const oldest = priced.length > 0 ? Math.min(priced[0]!.ts, token.createdAt) : token.createdAt;
  const firstPossible = Math.floor(oldest / bucket) * bucket;
  const n = Math.max(1, Math.min(want, Math.floor((lastOpen - firstPossible) / bucket) + 1));
  const firstOpen = lastOpen - (n - 1) * bucket;

  const floorTrack = reconstructFloorTrack(priced, floorNow, n, firstOpen, bucket);

  // Group into buckets in one pass; the tape is already time-ordered.
  const buckets: DemoTrade[][] = Array.from({ length: n }, () => []);
  for (const t of priced) {
    const i = Math.floor((t.ts - firstOpen) / bucket);
    if (i >= 0 && i < n) buckets[i]!.push(t);
  }

  // Opening price for the first bar: the last print before the window, else the
  // first print inside it, else spot. Never an invented "launch price".
  const before = priced.filter((t) => t.ts < firstOpen);
  let prevClose = before.length > 0 ? before[before.length - 1]!.price : (priced[0]?.price ?? spot);

  const out: Candle[] = [];
  for (let i = 0; i < n; i++) {
    const t = firstOpen + i * bucket;
    const fl = floorTrack[i]!;
    const inBucket = buckets[i]!;

    const o = Math.max(prevClose, fl);
    let c = inBucket.length > 0 ? inBucket[inBucket.length - 1]!.price : o;
    let h = o;
    let l = o;
    let v = 0;
    for (const trade of inBucket) {
      if (trade.price > h) h = trade.price;
      if (trade.price < l) l = trade.price;
      v += trade.ethAmount;
    }

    // Invariant 1, and it has to apply to a QUIET last bucket too. The tape's
    // prices are execution averages (gross ETH / tokens), while `spotPrice()` is
    // the marginal price off the reserves — so the last print does not equal spot
    // even when nothing has traded since. Anchoring only on a busy final bucket
    // left the chart ending a few percent away from the ticker.
    if (i === n - 1 && spot > 0) {
      c = spot;
      if (c > h) h = c;
      if (c < l) l = c;
    }
    // Invariant 3.
    c = Math.max(c, fl);
    h = Math.max(h, c, o);
    l = Math.max(fl, Math.min(l, o, c));

    out.push({ t, o, h, l, c, v, floor: fl });
    prevClose = c;
  }

  return out;
}

/**
 * The running floor at each bucket close.
 *
 * Walks the real floor lifts and apportions the total rise so the track ends on
 * the token's true current floor. `Math.max` against the running value makes the
 * monotonicity structural rather than incidental — the one property the whole
 * floor UI is a promise about.
 */
function reconstructFloorTrack(
  trades: readonly DemoTrade[],
  floorNow: number,
  n: number,
  firstOpen: number,
  bucket: number,
): number[] {
  const track = new Array<number>(n).fill(floorNow);
  if (!(floorNow > 0)) return track;

  const lifts = trades.filter((t) => t.floorLiftEth !== undefined && t.floorLiftEth > 0);
  if (lifts.length === 0) return track;

  // Cumulative lift ETH per bucket, plus everything that landed before the window.
  const perBucket = new Array<number>(n).fill(0);
  let beforeWindow = 0;
  let total = 0;
  for (const t of lifts) {
    const amount = t.floorLiftEth!;
    total += amount;
    const i = Math.floor((t.ts - firstOpen) / bucket);
    if (i < 0) beforeWindow += amount;
    else if (i < n) perBucket[i] += amount;
    else perBucket[n - 1] += amount;
  }
  if (!(total > 0)) return track;

  // The floor already in place when the window opened, scaled by the share of
  // lifetime lift ETH that predates it.
  const startFraction = beforeWindow / total;
  let running = floorNow * startFraction;
  let cumulative = beforeWindow;
  for (let i = 0; i < n; i++) {
    cumulative += perBucket[i]!;
    const target = floorNow * (cumulative / total);
    running = Math.max(running, target);
    track[i] = Math.min(running, floorNow);
  }
  // Anchor the last point on the value the contract actually reports.
  track[n - 1] = floorNow;
  return track;
}

/**
 * 48 closes over the last 24h, for the list/hero strips. Uses the same bucketing
 * as {@link buildCandlesFromTrades} so the strip and the chart agree.
 */
export function buildSparklineFromTrades(
  trades: readonly DemoTrade[],
  token: DemoToken,
  now = Date.now(),
): number[] {
  return sparkPair(trades, token, now).price;
}

/** The matching floor track for the 24h strip. */
export function buildFloorSparklineFromTrades(
  trades: readonly DemoTrade[],
  token: DemoToken,
  now = Date.now(),
): number[] {
  return sparkPair(trades, token, now).floor;
}

function sparkPair(
  trades: readonly DemoTrade[],
  token: DemoToken,
  now: number,
): { price: number[]; floor: number[] } {
  const spot = weiToNumber(token.spotPrice);
  const floorNow = weiToNumber(token.floorPrice);
  const start = Math.max(token.createdAt, now - DAY);
  const step = Math.max(1, (now - start) / (SPARK_POINTS - 1));

  const priced = trades.filter((t) => t.price > 0).sort(byChainOrder).reverse();
  const floorTrack = reconstructFloorTrack(priced, floorNow, SPARK_POINTS, start, step);

  const price: number[] = [];
  // Seed with the last print before the window so a quiet token is flat, not empty.
  let last = priced.find((t) => t.ts >= start)?.price ?? spot;
  for (const t of priced) {
    if (t.ts < start) last = t.price;
    else break;
  }

  let cursor = 0;
  for (let i = 0; i < SPARK_POINTS; i++) {
    const at = start + i * step;
    while (cursor < priced.length && priced[cursor]!.ts <= at) {
      last = priced[cursor]!.price;
      cursor += 1;
    }
    const v = i === SPARK_POINTS - 1 && spot > 0 ? spot : last;
    price.push(Math.max(v, floorTrack[i]!));
  }

  return { price, floor: floorTrack };
}
